<template>
  <v-container>
    <h1 class="text-h5 font-weight-bold mb-4">Métricas de Apadrinamiento</h1>

    <!-- Filtros -->
    <v-card class="mb-5 pa-4" outlined>
      <v-row align="center">
        <v-col cols="12" sm="4">
          <v-menu
            v-model="menuStart"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="startDate"
                label="Fecha inicio"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                hide-details
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="startDate"
              :max="endDate"
              no-title
              @input="menuStart = false"
            />
          </v-menu>
        </v-col>

        <v-col cols="12" sm="4">
          <v-menu
            v-model="menuEnd"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="endDate"
                label="Fecha fin"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                hide-details
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="endDate"
              :min="startDate"
              no-title
              @input="menuEnd = false"
            />
          </v-menu>
        </v-col>

        <v-col cols="12" sm="4" class="d-flex justify-end">
          <v-btn color="primary" :loading="loading" @click="loadMetrics">
            <v-icon left>mdi-refresh</v-icon> Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Tarjetas resumen -->
    <v-row class="mb-5">
      <v-col cols="12" sm="6">
        <v-card outlined class="pa-4 d-flex align-center">
          <v-avatar color="primary" size="48" class="mr-4">
            <v-icon dark>mdi-heart</v-icon>
          </v-avatar>
          <div>
            <div class="caption grey--text text--darken-1">Apadrinamientos activos hoy</div>
            <div class="text-h4 font-weight-bold primary--text">
              <span v-if="!loading">{{ summary.totalDirectSponsorships }}</span>
              <v-progress-circular v-else indeterminate color="primary" size="28" width="3" />
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6">
        <v-card outlined class="pa-4 d-flex align-center">
          <v-avatar color="teal" size="48" class="mr-4">
            <v-icon dark>mdi-account-group</v-icon>
          </v-avatar>
          <div>
            <div class="caption grey--text text--darken-1">Planes solidarios hoy</div>
            <div class="text-h4 font-weight-bold teal--text">
              <span v-if="!loading">{{ summary.totalSolidarioPlans }}</span>
              <v-progress-circular v-else indeterminate color="teal" size="28" width="3" />
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Gráfico de líneas -->
    <v-card outlined class="mb-5" min-height="480">
      <v-card-title class="subtitle-1 font-weight-bold d-flex align-center">
        <v-icon left small>mdi-chart-line</v-icon>
        Comportamiento en el tiempo
        <span class="body-2 font-weight-regular grey--text ml-2">
          {{ startDate }} → {{ endDate }}
        </span>
      </v-card-title>
      <v-card-text class="pb-6">
        <div v-if="!loading && timeSeries.length > 0" style="position: relative; height: 300px; width: 100%;">
          <line-chart :chart-data="chartData" :options="chartOptions" />
        </div>
        <div v-else-if="!loading" class="text-center grey--text py-8">
          Sin datos para el período seleccionado
        </div>
        <div v-else class="d-flex justify-center align-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>
      </v-card-text>
    </v-card>

    <!-- Ranking por empresa: requiere `sponsorship-metrics:view-contracts`. -->
    <v-card v-if="canSeeContracts" outlined class="mb-5">
      <v-card-title class="subtitle-1 font-weight-bold d-flex align-center">
        <v-icon left small>mdi-trophy</v-icon>
        Ranking por empresa
      </v-card-title>
      <v-card-text class="pa-0">
        <div v-if="loadingRanking" class="d-flex justify-center align-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="ranking.length === 0" class="text-center grey--text py-8">
          Sin datos disponibles
        </div>
        <v-list v-else dense>
          <v-list-item v-for="(item, index) in ranking" :key="item.companyId">
            <v-avatar size="28" :color="rankColor(index)" class="mr-3 flex-shrink-0">
              <span class="white--text caption font-weight-bold">{{ index + 1 }}</span>
            </v-avatar>
            <v-list-item-content>
              <v-list-item-title class="body-2">{{ item.companyName }}</v-list-item-title>
              <v-progress-linear
                :value="ranking[0].activeSponsorships ? (item.activeSponsorships / ranking[0].activeSponsorships) * 100 : 0"
                :color="rankColor(index)"
                height="4"
                rounded
                class="mt-1"
              />
            </v-list-item-content>
            <v-list-item-action class="ml-4">
              <span class="body-2 font-weight-bold">{{ item.activeSponsorships }}</span>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="snackbar" color="error" top>{{ snackbarText }}</v-snackbar>
  </v-container>
</template>

<script>
import { Line } from 'vue-chartjs';
import SponsorshipService from '../services/sponsorship.service';
import { can } from '../utils/permissions';

const LineChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      deep: true,
      handler(newData) {
        if (this.$data._chart) {
          this.$data._chart.destroy();
        }
        this.renderChart(newData, this.options);
      }
    }
  }
};

export default {
  name: 'SponsorshipMetrics',
  components: { LineChart },
  data() {
    const today = new Date();
    return {
      startDate: '2024-01-01',
      endDate: today.toISOString().substring(0, 10),
      menuStart: false,
      menuEnd: false,
      loading: false,
      loadingRanking: false,
      canSeeContracts: can('sponsorship-metrics:view-contracts'),
      ranking: [],
      summary: {
        totalDirectSponsorships: 0,
        totalSolidarioPlans: 0
      },
      timeSeries: [],
      snackbar: false,
      snackbarText: ''
    };
  },
  computed: {
    chartData() {
      const labels = this.timeSeries.map(r => this.formatPeriod(r.period));
      return {
        labels,
        datasets: [
          {
            label: 'Apadrinamientos directos',
            data: this.timeSeries.map(r => r.directSponsorships || 0),
            borderColor: '#1976D2',
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            pointBackgroundColor: '#1976D2',
            pointRadius: 5,
            borderWidth: 2,
            fill: true,
            tension: 0.3
          },
          {
            label: 'Planes solidarios',
            data: this.timeSeries.map(r => r.solidarioPlans || 0),
            borderColor: '#00897B',
            backgroundColor: 'rgba(0, 137, 123, 0.08)',
            pointBackgroundColor: '#00897B',
            pointRadius: 5,
            borderWidth: 2,
            fill: true,
            tension: 0.3
          }
        ]
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom',
          labels: { fontFamily: 'Roboto, sans-serif', fontSize: 12, usePointStyle: true, padding: 20 }
        },
        scales: {
          xAxes: [{ stacked: true, gridLines: { display: false } }],
          yAxes: [{
            stacked: true,
            ticks: { beginAtZero: true, precision: 0 },
            gridLines: { color: 'rgba(0,0,0,0.05)' }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false
        }
      };
    }
  },
  async created() {
    await this.loadAll();
  },
  methods: {
    async loadAll() {
      await Promise.all([this.loadMetrics(), this.canSeeContracts ? this.loadRanking() : null]);
    },

    async loadMetrics() {
      this.loading = true;
      const res = await SponsorshipService.getMetrics(this.startDate, this.endDate);
      if (res.status === 'ok') {
        this.summary = res.data.summary;
        this.timeSeries = res.data.timeSeries;
      } else {
        this.snackbarText = res.message || 'Error al cargar las métricas';
        this.snackbar = true;
      }
      this.loading = false;
    },
    async loadRanking() {
      this.loadingRanking = true;
      const res = await SponsorshipService.getRankingByCompany();
      if (res.status === 'ok') {
        this.ranking = res.data;
      } else {
        this.snackbarText = res.message || 'Error al cargar el ranking';
        this.snackbar = true;
      }
      this.loadingRanking = false;
    },
    rankColor(index) {
      if (index === 0) return '#1976D2';
      if (index === 1) return '#64B5F6';
      if (index === 2) return '#90A4AE';
      return '#BDBDBD';
    },
    formatPeriod(period) {
      const [year, month] = period.split('-');
      const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      return `${months[parseInt(month, 10) - 1]} ${year}`;
    }
  }
};
</script>
